<template>
  <div class="contract-data">
    <h2 class="contract-data__headline">{{ t('settings.contractData.serviceProvider') }}</h2>

    <div class="contract-data__field contract-data__field--2-columns">
      <h3 class="contract-data__label">{{ t('settings.contractData.address') }}</h3>
      <address>
        <div>{{ cubeSupplier.name}}</div>
        <div>
          {{ cubeSupplier.street  }}
          {{ cubeSupplier.street_number }}
        </div>
        <div>
          {{ cubeSupplier.zip_code }}
          {{ cubeSupplier.city }}
        </div>
      </address>
    </div>
    <div class="contract-data__field contract-data__field--2-columns">
      <h3 class="contract-data__label">{{ t('settings.contractData.support') }}</h3>
      <a
        class="contract-data__support"
        :href="`tel:${cubeSupplier.phone}`"
        >{{ cubeSupplier.phone }}</a
      >
      <a
        class="contract-data__support"
        :href="`mailto:${cubeSupplier.email}}`"
        >{{ cubeSupplier.email }}</a
      >
    </div>

    <h2 class="contract-data__headline">{{ t('settings.contractData.contract') }}</h2>

    <div class="contract-data__field contract-data__field--2-columns">
      <h3 class="contract-data__label">{{ t('settings.contractData.nickname') }}</h3>
      {{ !editNickName ? nickName : '' }}
      <span class="nickname-settings" v-if="editNickName">
        <input
          id="input-nickname"
          type="text"
          name="nickname"
          :placeholder="nickName"
          v-model="nickNameNew"
          :class="{ 'form-item--error': nickNameErrorMessage }"
        />
        <button
          type="button"
          class="button button--small button--primary"
          @click="sendNewNickName"
        >
          {{ t('global.save') }}
        </button>
      </span>
      <a @click.prevent="toggleEditNickName()"><IconEdit /></a>
      <span v-if="nickNameErrorMessage" :class="{ 'form-item--error': nickNameErrorMessage }">
        {{ nickNameErrorMessage}}
      </span>
    </div>

    <div class="contract-data__field contract-data__field--2-columns">
      <h3 class="contract-data__label">{{ t('settings.contractData.connectTariff') }}</h3>
      {{ flexPlanName }}
    </div>

    <div class="contract-data__field contract-data__field--2-columns">
      <h3 class="contract-data__label">{{ t('settings.contractData.meterPointId') }}</h3>
      {{ contractData.meterPointId }}
    </div>

    <div class="contract-data__field contract-data__field--2-columns">
      <h3 class="contract-data__label">{{ t('settings.contractData.contractNumber') }}</h3>
      {{ contractData.contractId }}
    </div>

    <div class="contract-data__field">
      <h3 class="contract-data__label">{{ t('settings.contractData.name') }}</h3>
      {{ contractData.fullName }}
    </div>

    <div class="contract-data__field contract-data__field--2-columns">
      <h3 class="contract-data__label">{{ t('settings.contractData.email') }}</h3>
      {{ contractData.email }}
    </div>

    <div class="contract-data__field contract-data__field--2-columns">
      <h3 class="contract-data__label">{{ t('settings.contractData.phone') }}</h3>
      {{ contractData.phone }}
    </div>

    <template v-if="contractData.contractId && showReferral">
      <div class="contract-data__field contract-data__field--2-columns">
        <h3>{{ t('settings.contractData.referralCodeTitle') }}</h3>
        <p>
          <a
            :href="referralQR"
            target="_blank"
            :aria-label="t('settings.contractData.referralQrAria')"
          >
            <qrcode-vue :value="referralQR" />
          </a>
        </p>
        <p>
          <LinkButton
            :href="'https://api.whatsapp.com/send?text=' + encodeURIComponent(t('settings.contractData.whatsappMessage') + referralQR)"
            :text="t('settings.contractData.whatsappInvite')"
            icon-left="WhatsApp"
            :extra-classes="['button--whatsapp']"
            target="_blank"
          />
        </p>
        <p class="contract-data__qr-desc">
          {{ t('settings.contractData.referralDescPre') }}
          <a
            :href="referralQR"
            target="_blank"
            :aria-label="t('settings.contractData.referralQrAria')"
          >{{ t('settings.contractData.referralDescLink') }}</a>.
          {{ t('settings.contractData.referralDescPost') }}
        </p>
      </div>
    </template>

    <h2 class="contract-data__headline">{{ t('settings.contractData.deliveryAddress') }}</h2>

    <div class="contract-data__field">
      <h3 class="contract-data__label">{{ t('settings.contractData.address') }}</h3>
      {{ contractData.cubeAddress.address }}
    </div>

    <div class="contract-data__field contract-data__field--2-columns">
      <h3 class="contract-data__label">{{ t('settings.contractData.zipCity') }}</h3>
      {{ contractData.cubeAddress.zip }}
      {{ contractData.cubeAddress.city }}
    </div>

    <div class="contract-data__field contract-data__field--2-columns">
      <h3 class="contract-data__label">{{ t('settings.contractData.country') }}</h3>
      {{ contractData.cubeAddress.country }}
    </div>

    <h2 class="contract-data__headline">{{ t('settings.contractData.billingAddress') }}</h2>

    <template v-if="differentBillingAddress">
      <div class="contract-data__field">
        <h3 class="contract-data__label">{{ t('settings.contractData.address') }}</h3>
        {{ contractData.billingAddress.address }}
      </div>

      <div class="contract-data__field contract-data__field--2-columns">
        <h3 class="contract-data__label">{{ t('settings.contractData.zipCity') }}</h3>
        {{ contractData.billingAddress.zip }}
        {{ contractData.billingAddress.city }}
      </div>

      <div class="contract-data__field contract-data__field--2-columns">
        <h3 class="contract-data__label">{{ t('settings.contractData.country') }}</h3>
        {{ contractData.billingAddress.country }}
      </div>
    </template>
    <template v-else>
      <p>{{ t('settings.contractData.sameBillingAddress') }}</p>
    </template>
  </div>
</template>

<script setup lang="ts">
  import IconEdit from '@/components/icons/Edit.vue'
  import LinkButton from '@/components/LinkButton.vue'
  import QrcodeVue from 'qrcode.vue'
  import { useCubeStore } from '@/stores/useCubeStore'
  import { useSettingsStore } from '@/stores/useSettingsStore'
  import { computed, ref, watch } from 'vue'
  import { useStrings } from '@/composables/useStrings.ts'
  import { useNotificationsStore } from '@/stores/useNotificationsStore'

  const cubeStore = useCubeStore()
  const settingsStore = useSettingsStore()
  const notificationStore = useNotificationsStore()
  const { t } = useStrings()

  const nickName = computed(() => cubeStore.nickName)
  const nickNameErrorMessage: import('vue').Ref<string | null> = ref(null)
  const editNickName = ref(false)
  const nickNameNew = ref(cubeStore.nickName)

  const flexPlanName = computed(() => cubeStore.planName)
  const contractData = computed(() => settingsStore.contractData)

  watch(contractData, (newValue) => {
    // Since the nickName the efriends backend can differ from that returned
    // from reem probably due to the changed name not being passed on somewhere
    // we need to update the name we display when the contract data is received.
    nickNameNew.value = newValue.nickName
  })

  const cubeSupplier = computed(() => cubeStore.supplier)

  const differentBillingAddress = computed(() => {
    if (
      contractData.value.cubeAddress.address !== contractData.value.billingAddress.address ||
      contractData.value.cubeAddress.zip !== contractData.value.billingAddress.zip ||
      contractData.value.cubeAddress.city !== contractData.value.billingAddress.city ||
      contractData.value.cubeAddress.country !== contractData.value.billingAddress.country
    ) {
      return true
    }
    return false
  })

  const referralQR = computed(() => {
    if (contractData.value.contractId) {
      return `https://www.efriends.at/efriend-werden.html?referral=${encodeURIComponent(
        contractData.value.contractId
      )}`
    }
    return null
  })

  const showReferral = computed(() => {
    // TODO Clean up, document,...
    return cubeStore.supplier.name === 'eFriends Energy GmbH'
  })

  const toggleEditNickName = () => {
    nickNameNew.value = nickName.value
    nickNameErrorMessage.value = null;
    editNickName.value = !editNickName.value
  }

  const sendNewNickName = async () => {
    try {
      await cubeStore.sendNickName(nickNameNew.value)

      nickNameNew.value = cubeStore.nickName
      nickNameErrorMessage.value = null;

      toggleEditNickName()
    } catch (err: any) {
      const errorType = err.response?.data || "CANNOT_CHANGE_NICKNAME"
      const errorMessage = t(`errors.${errorType}`);

      nickNameErrorMessage.value = errorMessage;
      notificationStore.showNotification({ body: errorMessage })
    }
  }
</script>

<style lang="scss">
.contract-data {
  display: flex;
  flex-wrap: wrap;

  &__headline {
    margin-top: $spacer20;
    margin-bottom: $spacer05;
    flex-basis: 100%;

    &:first-child {
      margin-top: 0;
    }
  }

  &__field {
    font-size: $font-size-lg;
    flex-basis: 100%;
    margin-bottom: $spacer05;

    &--2-columns {
      @media (min-width: $bp-lg) {
        flex-basis: 50%;
      }
    }
  }

  &__label {
    margin-bottom: 0;
    font-size: $font-size-base;
    font-weight: inherit;
    color: $grey-dark;
    line-height: $line-height-sm;
  }

  &__support {
    display: block;
    color: inherit;
  }
  &__qr-desc {
    font-size: 1rem;
  }
}

.nickname-settings > input:nth-child(1) {
  display: inline-block;
  width: 60%;
  font-size: 1.2rem;
  height: 2em;
  margin-right: 0.5em;
}
.button--small {
  margin-right: 0.5em;
}
</style>
